import React from "react";
import GetAuthContext from "../../shared/context/AuthContext";
import { ACTIONS_LIST } from "../../shared/utils/constants";

import { accessControl } from "@unified-dashboard/src/core/access-control";

/**
 * @deprecated Transitional adapter over the access-control service.
 * New code must use `useHasAccess()` (src/shared/hooks/useHasAccess.ts) or `<Guard>`
 * (src/components/composed/Guard.tsx). Existing call sites are being migrated
 * page by page; this file is deleted when the last one is gone.
 *
 * All policy (RBAC evaluation, UI-config lookup, tenant mode routing) lives in
 * accessControl — this component only keeps the legacy rendering concerns
 * (permission-denied page, disabled-not-hidden, prop cloning).
 */

interface Props {
  requiredPage: string; // Page permission required to see the component
  children: JSX.Element; // The component to be rendered
  checkPermission?: ({
    // Custom function which can be provided to check the permission
    requiredPage,
    requiredFunctions,
    sourceEntityId,
    permissions
  }: {
    requiredPage: string;
    sourceEntityId?: string | Array<string>;
    requiredFunctions: Array<(typeof ACTIONS_LIST)[keyof typeof ACTIONS_LIST]>;
    permissions: any;
  }) => boolean;
  requiredFunctions?: Array<(typeof ACTIONS_LIST)[keyof typeof ACTIONS_LIST]>; // The functional apis the user should have access to render this component
  showPermissionDenied?: boolean; // Whether to show permission denied or a blank page
  sourceEntityId?: string | Array<string>; // If present, the source entity id for which the access to functional api should be present
  some?: boolean; // If true, the component will be rendered if the user has access to any of the functional apis
  isUserType?: boolean;
  disableNotHide?: boolean;
  hideIfRequiredFunctionsPresent?: boolean; // If true, the component will not be rendered if requiredFunctions are present and user does not have access to any of them
}

function PermissionDenied() {
  return (
    <div className="flex flex-row items-center justify-center w-full h-full">
      <p className="flex text-3xl font-semibold py-8">Permission Denied</p>
    </div>
  );
}

export default function PrivateComponent({
  children,
  requiredPage,
  checkPermission,
  requiredFunctions = [],
  showPermissionDenied = false, // Used only for pages and tabs - used to determine if page or component
  sourceEntityId,
  some = false,
  disableNotHide = false,
  hideIfRequiredFunctionsPresent = false
}: Props) {
  const { userState } = GetAuthContext();
  const { permissions = {} } = userState || {};

  const isAllowed = accessControl.can(
    {
      page: requiredPage,
      entityIds: sourceEntityId,
      actions: requiredFunctions,
      some,
      forbidWhenActionsPresent: hideIfRequiredFunctionsPresent
    },
    permissions,
    checkPermission
      ? () =>
          checkPermission({
            requiredPage,
            requiredFunctions,
            permissions,
            sourceEntityId
          })
      : undefined
  );

  if (isAllowed || disableNotHide) {
    let props = {};

    if (showPermissionDenied) {
      // indicates that this is a page
      props = { pageName: requiredPage };
    } else if (!isAllowed && disableNotHide) {
      props = {
        disabled: disableNotHide,
        style: { cursor: "not-allowed", opacity: "0.5" }
      };
    }

    return (
      <>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, props)
        )}
      </>
    );
  }

  if (showPermissionDenied) {
    return <PermissionDenied />;
  }

  return null;
}
